/**
 * Die Daten, die der Überblick und die Tabs des Bereichs "Schule" gemeinsam
 * brauchen (BE-04) - einmal berechnet, damit jede Seite dieselben Zahlen zeigt.
 */
import { useEffect } from 'react';

import { ensureSchoolSetup } from '@/db/repositories/school';
import { klausurDatum } from '@/domain/school/exams';
import { schnittPunkte, gesamtSchnitt } from '@/domain/school/grades';
import { ferienAm, naechsteFerien } from '@/domain/school/ferien';
import { stundenDerWoche } from '@/domain/school/timetable';
import { abWocheVon } from '@/domain/school/weeks';
import { nachFrist, useHausaufgaben, useKlausuren, useNoten, useSchoolSettings, useStunden, useSubjects } from '@/modules/school/useSchool';
import { today, weekday } from '@/lib/datetime';

export function useSchuleDaten() {
  // Beim ersten Oeffnen die Einstellungen aus dem Cockpit uebernehmen.
  useEffect(() => { void ensureSchoolSetup(); }, []);
  const heute = today();
  const settings = useSchoolSettings();
  const faecher = useSubjects();
  const offen = useHausaufgaben().filter((t) => t.status !== 'erledigt').sort(nachFrist);
  const stunden = useStunden(settings);
  const klausuren = useKlausuren();
  const naechsteKlausur = klausuren.find((k) => klausurDatum(k) >= heute);
  const noten = useNoten();
  const system = settings?.notensystem ?? 'punkte';
  const schnittVon = (fachId: string) => schnittPunkte(noten.filter((n) => n.subjectId === fachId));
  const gesamt = gesamtSchnitt(faecher.map((f) => schnittVon(f.id)));
  const woche = settings?.abWochen ? abWocheVon(heute, settings.aWocheAb) : null;
  // In den Ferien fallen die Stunden aus (F-22).
  const ferienHeute = ferienAm(settings?.ferien ?? [], heute);
  const kommendeFerien = naechsteFerien(settings?.ferien ?? [], heute);
  const heuteStunden = ferienHeute ? [] : stundenDerWoche(stunden, woche).get(weekday(heute)) ?? [];
  return { heute, settings, faecher, offen, stunden, klausuren, naechsteKlausur, noten, system, schnittVon, gesamt, woche, ferienHeute, kommendeFerien, heuteStunden };
}

export type SchuleDaten = ReturnType<typeof useSchuleDaten>;
